import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { Alert, AlertKind, Spinner, Table, type TableColumn, TableRow } from "@prequel/shared-components";

import FilterBar, { type FilterField } from "./FilterBar";

import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE, parseNumberParam } from "@/types/pagination";

export type { FilterField };

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Every list hook returns one page, flattened the same way.
export type UsePaginatedQueryResult<T> = {
  data: T[] | undefined;
  isLoading?: boolean;
  error?: unknown;
  hasNextPage?: boolean;
  nextCursor?: string;
};

export type ResourceTableProps<T> = {
  columns: TableColumn[];
  useQuery: (
    params: { cursor?: string; pageSize?: number },
    filters?: Record<string, string>,
  ) => UsePaginatedQueryResult<T>;
  rowMapper: (item: T) => TableRow | null;
  rowFilter?: (item: T) => boolean;
  rowComparator?: (a: T, b: T) => number;
  emptyMessage: string;
  filterFields?: FilterField[];
  loadingMessage?: string;
  selectable?: boolean;
  selectedIds?: (string | number)[];
  onSelectionChange?: (ids: (string | number)[]) => void;
  selectionLabel?: (row: TableRow) => string;
  // Rendered between the filter bar and the table, e.g. a selection count and a bulk action.
  actions?: React.ReactNode;
};

// Smart table wrapper on top of the shared presentational Table component.
// Orchestrates data fetching, URL-persisted pagination, filtering.
const ResourceTable = <T,>({
  columns,
  useQuery,
  rowMapper,
  rowFilter,
  rowComparator,
  emptyMessage,
  filterFields,
  loadingMessage,
  selectable,
  selectedIds,
  onSelectionChange,
  selectionLabel,
  actions,
}: ResourceTableProps<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const cursor = searchParams.get("cursor") || undefined;
  const pageSize = parseNumberParam(searchParams.get("page_size"), DEFAULT_PAGE_SIZE, {
    min: 1,
    max: MAX_PAGE_SIZE,
  });

  // Derive backend filters from URL params + filterFields config
  const backendFilters: Record<string, string> = useMemo(() => {
    if (!filterFields) {
      return {};
    }
    const result: Record<string, string> = {};
    for (const field of filterFields) {
      const value = searchParams.get(field.queryParam);
      if (value) {
        result[field.queryParam] = value;
      }
    }
    return result;
  }, [searchParams, filterFields]);

  const {
    data: pageItems,
    isLoading,
    error,
    hasNextPage: canGoNext,
    nextCursor,
  } = useQuery({ cursor, pageSize }, filterFields ? backendFilters : undefined);
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([undefined]);
  const [currentPage, setCurrentPage] = useState(1);

  // Reset displayed page when filters change.
  const filterKey = JSON.stringify(backendFilters);
  const prevFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (filterKey !== prevFilterKeyRef.current) {
      setCurrentPage(1);
      setCursorHistory([undefined]);
      prevFilterKeyRef.current = filterKey;
    }
  }, [filterKey]);

  // A navigation can drop the cursor while this table stays mounted, so the controls have to
  // follow the first page back rather than keep describing the page they were on.
  useEffect(() => {
    if (!cursor) {
      setCurrentPage(1);
      setCursorHistory([undefined]);
    }
  }, [cursor]);

  const rows: TableRow[] = useMemo(() => {
    if (!pageItems) {
      return [];
    }
    const filtered = rowFilter ? pageItems.filter(rowFilter) : pageItems;
    const sorted = rowComparator ? [...filtered].sort(rowComparator) : filtered;
    return sorted.map(rowMapper).filter((r): r is TableRow => !!r);
  }, [pageItems, rowMapper, rowFilter, rowComparator]);

  // Update pagination params while preserving non-pagination URL params (e.g. filters)
  const updatePaginationParams = (updates: Record<string, string | undefined>) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === undefined) {
            params.delete(key);
          } else {
            params.set(key, value);
          }
        }
        return params;
      },
      { replace: true },
    );
  };

  const handleNextPage = () => {
    if (!nextCursor) {
      return;
    }

    setCursorHistory([...cursorHistory, nextCursor]);
    setCurrentPage((page) => page + 1);
    updatePaginationParams({ cursor: nextCursor });
  };

  const handlePrevPage = () => {
    if (cursorHistory.length <= 1) {
      return;
    }

    const newHistory = cursorHistory.slice(0, -1);
    const prevCursor = newHistory[newHistory.length - 1];

    setCursorHistory(newHistory);
    setCurrentPage((page) => Math.max(1, page - 1));
    updatePaginationParams({ cursor: prevCursor ?? undefined });
  };

  const handlePageSizeChange = (size: number) => {
    setCurrentPage(1);
    setCursorHistory([undefined]);
    updatePaginationParams({ cursor: undefined, page_size: `${size}` });
  };

  const filterBar =
    filterFields || actions ? (
      <div className="flex items-center justify-between gap-4">
        {filterFields ? (
          <FilterBar filterFields={filterFields} searchParams={searchParams} setSearchParams={setSearchParams} />
        ) : (
          <div />
        )}
        {actions}
      </div>
    ) : null;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        {filterBar}
        {loadingMessage && <Alert kind={AlertKind.INFO}>{loadingMessage}</Alert>}
        <div className="flex items-center justify-center rounded-md border border-gray-200 bg-white p-8">
          <Spinner size="sm" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        {filterBar}
        <Alert kind={AlertKind.ERROR}>Failed to load data.</Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {filterBar}
      <Table
        columns={columns}
        rows={rows}
        emptyState={emptyMessage}
        isPaginated
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageSizeChange={handlePageSizeChange}
        onNextPage={canGoNext ? handleNextPage : undefined}
        onPrevPage={cursorHistory.length > 1 ? handlePrevPage : undefined}
        currentPage={currentPage}
        selectable={selectable}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        selectionLabel={selectionLabel}
      />
    </div>
  );
};

export default ResourceTable;
